import React, { useState, useRef, useCallback, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { normalizeAssistantText } from '@hector/agent-text-normalizer';
import { Copy, Check, AlertTriangle, Info, CheckCircle2 } from 'lucide-react';
import type { Ordre } from '@hector/shared-types';
import { Source, SourceTrigger, SourceContent } from './Source';
import { HectorLink } from './HectorLink';
import { MermaidDiagram } from './MermaidDiagram';
import { VisualizerRenderer } from '../visualization/VisualizerRenderer';

interface ParsedSegment {
  type: 'markdown' | 'visualizer';
  content: string;
}

/**
 * Split content into alternating markdown and visualizer segments.
 * Visualizer fences: ```visualizer\n...\n```
 */
function extractVisualizerBlocks(text: string): ParsedSegment[] {
  const segments: ParsedSegment[] = [];
  const regex = /```visualizer\n([\s\S]*?)```/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'markdown', content: text.slice(lastIndex, match.index) });
    }
    segments.push({ type: 'visualizer', content: match[1] ?? '' });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'markdown', content: text.slice(lastIndex) });
  }

  return segments;
}

const CALLOUT_STYLES = {
  warning: {
    container: 'bg-amber-50 border-l-[3px] border-amber-400 rounded-r-lg px-3.5 py-2.5 my-3',
    icon: <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />,
  },
  info: {
    container: 'bg-blue-50 border-l-[3px] border-blue-400 rounded-r-lg px-3.5 py-2.5 my-3',
    icon: <Info className="h-4 w-4 text-accent-blue shrink-0 mt-0.5" />,
  },
  success: {
    container: 'bg-emerald-50 border-l-[3px] border-emerald-400 rounded-r-lg px-3.5 py-2.5 my-3',
    icon: <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />,
  },
} as const;

type CalloutType = keyof typeof CALLOUT_STYLES;

/**
 * Le `type` d'un `<hector-callout>` est écrit par le LLM, donc non fiable : il
 * émet parfois `danger`, `error`, `note`, `Warning` ou `type=""`. Avant ce
 * garde, un simple cast laissait passer la valeur, `CALLOUT_STYLES[type]`
 * valait `undefined` et `style.container` faisait planter le rendu du message
 * — message persisté, donc crash à chaque rechargement de la conversation
 * (incident prod 2026-09-10). Un type inconnu retombe sur `info`.
 */
function toCalloutType(raw: string | undefined): CalloutType | undefined {
  const normalized = raw?.trim().toLowerCase();
  return normalized !== undefined && Object.hasOwn(CALLOUT_STYLES, normalized)
    ? (normalized as CalloutType)
    : undefined;
}

interface HectorBlock {
  type: 'callout' | 'quote';
  calloutType?: CalloutType;
  source?: string;
  content: string;
}

interface ContentSegment {
  type: 'text' | 'block';
  content: string;
  block?: HectorBlock;
}

/**
 * Extract <hector-callout> and <hector-quote> blocks from text,
 * splitting into alternating text/block segments.
 */
function extractHectorBlocks(text: string): ContentSegment[] {
  const segments: ContentSegment[] = [];
  const regex = /<hector-(callout|quote)(?:\s+type="([^"]*)")?(?:\s+source="([^"]*)")?\s*>([\s\S]*?)<\/hector-\1>/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    }
    const [, tag, calloutType, source, content] = match;
    segments.push({
      type: 'block',
      content: content ?? '',
      block: {
        type: tag as 'callout' | 'quote',
        calloutType: toCalloutType(calloutType),
        source: source ?? undefined,
        content: content ?? '',
      },
    });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'text', content: text.slice(lastIndex) });
  }

  return segments;
}

/**
 * Wrapper that adds a copy button below any table element.
 * Copies as HTML (for Word/Docs) + TSV (plain text fallback).
 */
function CopyableTable({ children }: { children: React.ReactNode }) {
  const [isCopied, setIsCopied] = useState(false);
  const tableRef = useRef<HTMLDivElement>(null);

  const handleCopy = useCallback(async () => {
    const table = tableRef.current?.querySelector('table');
    if (!table) return;

    try {
      const rows = Array.from(table.querySelectorAll('tr'));
      const extractRow = (row: Element) =>
        Array.from(row.querySelectorAll('th, td')).map(c => (c.textContent || '').replace(/\s+/g, ' ').trim());

      const tsv = rows.map(r => extractRow(r).join('\t')).join('\n');

      let html = '<table border="1" cellpadding="5" cellspacing="0" style="border-collapse:collapse;">';
      for (const row of rows) {
        html += '<tr>';
        for (const cell of Array.from(row.querySelectorAll('th, td'))) {
          const tag = cell.tagName.toLowerCase();
          const style = tag === 'th'
            ? 'background-color:#f0f0f0;font-weight:bold;padding:8px;border:1px solid #ccc;'
            : 'padding:8px;border:1px solid #ccc;';
          const text = (cell.textContent || '').replace(/\s+/g, ' ').trim();
          const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
          html += `<${tag} style="${style}">${escaped}</${tag}>`;
        }
        html += '</tr>';
      }
      html += '</table>';

      if (navigator.clipboard && window.ClipboardItem) {
        const htmlBlob = new Blob([html], { type: 'text/html' });
        const textBlob = new Blob([tsv], { type: 'text/plain' });
        await navigator.clipboard.write([new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob })]);
      } else {
        await navigator.clipboard.writeText(tsv);
      }

      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('[CopyableTable] copy failed:', err);
    }
  }, []);

  return (
    <div ref={tableRef}>
      {children}
      <div className="flex justify-end mt-1.5">
        <button
          type="button"
          onClick={handleCopy}
          className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border transition-all duration-200
            ${isCopied
              ? 'bg-grey-100 border-grey-200 text-grey-800'
              : 'bg-grey-50 border-grey-200 text-grey-600 hover:bg-grey-100 hover:text-grey-800'
            }`}
        >
          {isCopied ? <><Check className="h-3 w-3" /><span>Copié !</span></> : <><Copy className="h-3 w-3" /><span>Copier le tableau</span></>}
        </button>
      </div>
    </div>
  );
}

interface MarkdownRendererProps {
  content: string;
  className?: string;
  /** When true, strips incomplete XML tags and markdown markers at the end of content */
  isStreaming?: boolean;
}

/**
 * Markdown renderer for V3 chat messages.
 * Supports: bold, italic, headers, lists, tables, line breaks,
 * <source> tags, <hector-link> tags, markdown links, raw URLs.
 */
export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = '', isStreaming = false }) => {
  /**
   * Normalised content used as the input to every render path below.
   * Unified pipeline lives in `@hector/agent-text-normalizer` (shared with edition-v3):
   * stripping legacy <source>, canonicalising callouts/quotes, partial-cut while streaming, etc.
   * useMemo avoids re-normalising on every render of the streaming chat.
   */
  const cleanedContent = useMemo(
    () => normalizeAssistantText(content, { streaming: isStreaming }),
    [content, isStreaming]
  );

  /** Strip legacy <source> tags — all linking now uses <hector-link> */
  const stripSourceTags = (text: string): string => {
    return text.replace(/<source[^>]*>[\s\S]*?<\/source>/gi, '');
  };

  const isTableRow = (line: string): boolean => {
    const trimmed = line.trim();
    return trimmed.startsWith('|') && trimmed.endsWith('|');
  };

  const isTableSeparator = (line: string): boolean => {
    return /^\|[\s\-:|]+\|$/.test(line.trim());
  };

  const parseTableCells = (row: string): string[] => {
    return row.split('|').slice(1, -1).map(cell => cell.trim());
  };

  const renderTable = (tableLines: string[], startKey: number): { element: JSX.Element; endKey: number } => {
    const headers = parseTableCells(tableLines[0] ?? '');
    const rows = tableLines.slice(2).map(line => parseTableCells(line));
    let key = startKey;

    const element = (
      <CopyableTable key={key++}>
        <div className="my-3 overflow-x-auto">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="border-b border-grey-200">
                {headers.map((header, i) => (
                  <th key={`th-${i}`} className="px-2 py-1.5 text-left font-semibold text-grey-600 bg-grey-50">
                    {renderInlineFormatting(header)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={`tr-${rowIndex}`} className="border-b border-grey-100 last:border-b-0 hover:bg-grey-50/50">
                  {row.map((cell, cellIndex) => (
                    <td key={`td-${rowIndex}-${cellIndex}`} className="px-2 py-1.5 text-grey-800">
                      {renderInlineFormatting(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CopyableTable>
    );
    return { element, endKey: key };
  };

  const renderMarkdown = (text: string, opts?: { compact?: boolean }): JSX.Element[] => {
    const compact = opts?.compact ?? false;
    const cleanText = stripSourceTags(text);
    const lines = cleanText.split('\n');
    const elements: JSX.Element[] = [];
    let key = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i] ?? '';

      if (!line.trim()) {
        elements.push(<div key={key++} className={compact ? 'h-1' : 'h-3'} />);
        continue;
      }

      // Mermaid code blocks: ```mermaid ... ```
      if (line.trim().startsWith('```mermaid')) {
        let mermaidCode = '';
        let j = i + 1;
        while (j < lines.length && !(lines[j] ?? '').trim().startsWith('```')) {
          mermaidCode += (lines[j] ?? '') + '\n';
          j++;
        }
        if (j < lines.length) j++; // skip closing ```
        if (mermaidCode.trim()) {
          elements.push(<MermaidDiagram key={key++} code={mermaidCode} />);
        }
        i = j - 1;
        continue;
      }

      // HTML table block — render via dangerouslySetInnerHTML with scoped styles
      if (line.trim().startsWith('<table')) {
        let tableHtml = '';
        let j = i;
        while (j < lines.length) {
          tableHtml += (lines[j] ?? '') + '\n';
          if ((lines[j] ?? '').includes('</table>')) { j++; break; }
          j++;
        }
        elements.push(
          <CopyableTable key={key++}>
            <div
              className="my-3 overflow-x-auto [&_table]:w-full [&_table]:text-[13px] [&_table]:border-collapse [&_thead_tr]:border-b [&_thead_tr]:border-grey-200 [&_th]:px-2 [&_th]:py-1.5 [&_th]:text-left [&_th]:font-semibold [&_th]:text-grey-600 [&_th]:bg-grey-50 [&_tbody_tr]:border-b [&_tbody_tr]:border-grey-100 [&_tbody_tr:last-child]:border-b-0 [&_tbody_tr:hover]:bg-grey-50/50 [&_td]:px-2 [&_td]:py-1.5 [&_td]:text-grey-800"
              // DOMPurify: tableHtml est assemblé depuis l'output LLM qui peut contenir du HTML arbitraire —
              // sanitize avec allowlist table-only pour bloquer tout XSS injecté dans les balises <table>
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(tableHtml, {
                ALLOWED_TAGS: ['table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col', 'br', 'b', 'strong', 'em', 'i', 'span'],
                ALLOWED_ATTR: ['class', 'colspan', 'rowspan', 'scope'],
              }) }}
            />
          </CopyableTable>
        );
        i = j - 1;
        continue;
      }

      // Markdown table
      if (isTableRow(line) && i + 1 < lines.length && isTableSeparator(lines[i + 1] ?? '')) {
        const tableLines: string[] = [line];
        let j = i + 1;
        while (j < lines.length && (isTableRow(lines[j] ?? '') || isTableSeparator(lines[j] ?? ''))) {
          tableLines.push(lines[j] ?? '');
          j++;
        }
        const { element, endKey } = renderTable(tableLines, key);
        elements.push(element);
        key = endKey;
        i = j - 1;
        continue;
      }

      // Horizontal rules (---, ***, ___)
      if (/^(-{3,}|\*{3,}|_{3,})$/.test((line).trim())) {
        elements.push(<hr key={key++} className="border-t border-grey-200 my-4" />);
        continue;
      }

      // Blockquotes: consecutive lines starting with >
      // In compact mode (callouts/quotes), render as plain text without blockquote styling
      if (line.trim().startsWith('>')) {
        if (compact) {
          elements.push(
            <div key={key++} className="mb-0.5">
              {renderInlineFormatting(line.trim().replace(/^>\s?/, ''))}
            </div>
          );
          continue;
        }
        const quoteLines: string[] = [];
        let j = i;
        while (j < lines.length && (lines[j] ?? '').trim().startsWith('>')) {
          quoteLines.push((lines[j] ?? '').trim().replace(/^>\s?/, ''));
          j++;
        }
        elements.push(
          <blockquote key={key++} className="border-l-[3px] border-grey-200 bg-grey-50 rounded-r-lg pl-3.5 pr-3 py-2 my-3 text-grey-800 text-[13px] leading-relaxed italic">
            {renderInlineFormatting(quoteLines.join(' '))}
          </blockquote>
        );
        i = j - 1;
        continue;
      }

      // Headers
      const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
      if (headerMatch) {
        const level = (headerMatch[1] ?? '').length;
        const HeaderTag = `h${Math.min(level, 6)}` as keyof JSX.IntrinsicElements;
        const headerClass = compact
          ? (level <= 2 ? 'text-sm font-semibold mt-1 first:mt-0 mb-0.5' : 'text-xs font-semibold mt-1 first:mt-0 mb-0.5')
          : (level === 1 ? 'text-xl font-bold mt-6 first:mt-0 mb-2 pb-2 border-b border-grey-200' :
             level === 2 ? 'text-lg font-semibold mt-5 first:mt-0 mb-1.5' :
             level === 3 ? 'text-base font-semibold mt-4 first:mt-0 mb-1' :
             'text-sm font-medium mt-3 first:mt-0 mb-1');
        elements.push(
          <HeaderTag key={key++} className={headerClass}>
            {renderInlineFormatting(headerMatch[2] ?? '')}
          </HeaderTag>
        );
        continue;
      }

      // Lists
      const listMatch = line.match(/^(\s*)([-*+]|\d+\.)\s+(.+)$/);
      if (listMatch) {
        const indent = (listMatch[1] ?? '').length;
        const marker = listMatch[2] ?? '';
        const isOrdered = /^\d+\./.test(marker);
        const indentClass = indent > 0 ? `ml-${Math.min(Math.floor(indent / 2) + 4, 8)}` : 'ml-4';
        elements.push(
          <div key={key++} className={`${indentClass} mb-1`}>
            <span className="mr-2 text-grey-600">{isOrdered ? marker : '•'}</span>
            <span>{renderInlineFormatting(listMatch[3] ?? '')}</span>
          </div>
        );
        continue;
      }

      // Regular paragraph
      elements.push(
        <div key={key++} className="mb-2 last:mb-0">
          {renderInlineFormatting(line)}
        </div>
      );
    }

    return elements;
  };

  /**
   * Parse <hector-link> tags
   */
  const parseHectorLinks = (text: string): { parts: (string | JSX.Element)[]; hasLinks: boolean } => {
    const parts: (string | JSX.Element)[] = [];
    // WHY parsing par MAP d'attributs (et non un regex positionnel) : DeepSeek V4 émet
    // les attributs des tags Hector dans un ORDRE VARIABLE (ex. `id` avant `ordre`). Un
    // regex à position fixe (`ordre` puis `id`, `name` en 2e) laissait silencieusement
    // tomber des liens jp valides en texte brut (incident viewer admin 2026-06-18). On
    // matche l'enveloppe du tag, puis on lit les attributs par NOM — insensible à l'ordre.
    const hectorLinkRegex = /<hector-link\b([^>]*?)\/>/g;
    const attrRegex = /([a-z_]+)\s*=\s*"([^"]*)"/g;

    let lastIndex = 0;
    let match;
    let linkKey = 0;

    while ((match = hectorLinkRegex.exec(text)) !== null) {
      const attrs: Record<string, string> = {};
      let a: RegExpExecArray | null;
      attrRegex.lastIndex = 0;
      while ((a = attrRegex.exec(match[1] ?? '')) !== null) {
        attrs[a[1] as string] = a[2] as string;
      }
      const type = attrs.type;
      const ref = attrs.ref;
      const hasKnownType = type === 'web' || type === 'piece' || type === 'jp';
      // A tag becomes a chip if it carries a resolvable `ref` token (new grammar)
      // OR a known inline type (legacy pre-ref messages). Anything else is left as
      // plain text (on n'avance pas lastIndex → la slice suivante le conserve).
      if (!ref && !hasKnownType) continue;

      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      parts.push(
        <HectorLink
          key={`hector-link-${linkKey++}`}
          refToken={ref}
          type={hasKnownType ? (type as 'web' | 'piece' | 'jp') : undefined}
          name={attrs.name ?? ''}
          url={attrs.url}
          file_path={attrs.file_path}
          ordre={attrs.ordre as Ordre | undefined}
          id={attrs.id}
        />
      );
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return { parts, hasLinks: linkKey > 0 };
  };

  const renderInlineFormatting = (text: string): (string | JSX.Element)[] => {
    const { parts: hectorParts, hasLinks } = parseHectorLinks(text);

    if (hasLinks) {
      const result: (string | JSX.Element)[] = [];
      let keyOffset = 0;
      for (const part of hectorParts) {
        if (typeof part === 'string' && part.length > 0) {
          const formatted = renderInlineFormattingCore(part, keyOffset);
          result.push(...formatted);
          keyOffset += formatted.length;
        } else if (typeof part !== 'string') {
          result.push(part);
        }
      }
      return result.length > 0 ? result : [text];
    }

    return renderInlineFormattingCore(text, 0);
  };

  const renderInlineFormattingCore = (text: string, keyOffset: number): (string | JSX.Element)[] => {
    const result: (string | JSX.Element)[] = [];
    let currentIndex = 0;
    let key = keyOffset;

    // Match: bold/italic, markdown links, raw URLs, quoted text (« » or " ")
    const formatRegex = /(\*{1,3})(.*?)\1|\[([^\]]+)\]\(([^)]+)\)|(https?:\/\/[^\s<>"\]]+)|«\s*(.*?)\s*»|"\s*(.*?)\s*"/g;
    let match;

    while ((match = formatRegex.exec(text)) !== null) {
      if (match.index > currentIndex) {
        result.push(text.substring(currentIndex, match.index));
      }

      // Raw URL
      if (match[5]) {
        let url = match[5];
        let trailingChars = '';
        while (url.length > 0) {
          const lastChar = url[url.length - 1] ?? '';
          if (['.', ',', ';', ':', '!', '?'].includes(lastChar)) {
            trailingChars = lastChar + trailingChars;
            url = url.slice(0, -1);
          } else if (lastChar === ')') {
            const openCount = (url.match(/\(/g) || []).length;
            const closeCount = (url.match(/\)/g) || []).length;
            if (closeCount > openCount) {
              trailingChars = lastChar + trailingChars;
              url = url.slice(0, -1);
            } else break;
          } else break;
        }
        result.push(
          <Source key={`url-${key++}`} href={url}>
            <SourceTrigger showFavicon />
            <SourceContent title={extractTitleFromUrl(url)} description={url} />
          </Source>
        );
        if (trailingChars) result.push(trailingChars);
        currentIndex = match.index + match[0].length - trailingChars.length;
        continue;
      }
      // Markdown link [text](url)
      else if (match[3] && match[4]) {
        result.push(
          <Source key={`link-${key++}`} href={match[4]}>
            <SourceTrigger showFavicon>{match[3]}</SourceTrigger>
            <SourceContent title={match[3]} description={match[4]} />
          </Source>
        );
      }
      // Quoted text « » or " " → italic
      else if (match[6] != null) {
        result.push(<em key={key++} className="italic">«&nbsp;{match[6]}&nbsp;»</em>);
      }
      else if (match[7] != null) {
        result.push(<em key={key++} className="italic">"{match[7]}"</em>);
      }
      // Bold/italic
      else {
        const markers = match[1];
        const content = match[2];
        if (markers === '***') {
          result.push(<strong key={key++} className="font-bold italic">{content}</strong>);
        } else if (markers === '**') {
          result.push(<strong key={key++} className="font-bold">{content}</strong>);
        } else if (markers === '*') {
          result.push(<em key={key++} className="italic">{content}</em>);
        }
      }
      currentIndex = match.index + match[0].length;
    }

    if (currentIndex < text.length) {
      result.push(text.substring(currentIndex));
    }

    return result.length > 0 ? result : [text];
  };

  const extractTitleFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const domain = urlObj.hostname.replace('www.', '');
      const pathSegments = urlObj.pathname.split('/').filter(Boolean);
      if (pathSegments.length > 0) {
        const lastSegment = (pathSegments[pathSegments.length - 1] ?? '')
          .replace(/[-_]/g, ' ')
          .replace(/\.[^.]+$/, '');
        return `${domain} - ${lastSegment}`;
      }
      return domain;
    } catch {
      return url;
    }
  };

  const renderCallout = (block: HectorBlock, key: string) => {
    const style = CALLOUT_STYLES[block.calloutType ?? 'info'];
    return (
      <div key={key} className={style.container}>
        <div className="flex gap-2">
          {style.icon}
          <div className="text-grey-800 text-[13px] leading-relaxed min-w-0">
            {renderMarkdown(block.content, { compact: true })}
          </div>
        </div>
      </div>
    );
  };

  const renderQuote = (block: HectorBlock, key: string) => {
    return (
      <blockquote key={key} className="border-l-[3px] border-grey-200 bg-grey-50 rounded-r-lg pl-3.5 pr-3 py-2 my-3 text-grey-800 text-[13px] leading-relaxed italic">
        {renderMarkdown(block.content, { compact: true })}
        {block.source && (
          <footer className="mt-1.5 text-xs text-grey-600 not-italic font-medium">
            {block.source}
          </footer>
        )}
      </blockquote>
    );
  };

  /** Render a markdown string that may contain hector-callout/quote blocks */
  const renderWithBlocks = (text: string, keyPrefix: string): React.ReactNode[] => {
    const blockSegments = extractHectorBlocks(text);
    const hasBlocks = blockSegments.some(s => s.type === 'block');

    if (!hasBlocks) {
      return renderMarkdown(text);
    }

    return blockSegments.map((seg, i) => {
      if (seg.type === 'block' && seg.block) {
        if (seg.block.type === 'callout') {
          return renderCallout(seg.block, `${keyPrefix}-callout-${i}`);
        }
        return renderQuote(seg.block, `${keyPrefix}-quote-${i}`);
      }
      return <React.Fragment key={`${keyPrefix}-md-${i}`}>{renderMarkdown(seg.content)}</React.Fragment>;
    });
  };

  const vizSegments = useMemo(() => extractVisualizerBlocks(cleanedContent), [cleanedContent]);
  const hasVisualizer = vizSegments.some(s => s.type === 'visualizer');

  if (!hasVisualizer) {
    return (
      <div className={`markdown-content ${className}`}>
        {renderWithBlocks(cleanedContent, 'root')}
      </div>
    );
  }

  return (
    <div className={`markdown-content ${className}`}>
      {vizSegments.map((segment, i) => {
        if (segment.type === 'visualizer') {
          return <VisualizerRenderer key={`viz-${i}`} content={segment.content} />;
        }
        return <React.Fragment key={`md-${i}`}>{renderWithBlocks(segment.content, `seg-${i}`)}</React.Fragment>;
      })}
    </div>
  );
};
